


import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './UserCard.css';

const STATUS_CONFIG = {
  pending: {
    badge: 'badge-warning',
    icon: '⏳',
    label: 'Request Pending',
  },
  accepted: {
    badge: 'badge-success',
    icon: '✓',
    label: 'Swap Accepted',
  },
  rejected: {
    badge: 'badge-danger',
    icon: '✕',
    label: 'Request Rejected',
  },
};

const UserCard = ({ user, swap, currentUserId, onRequestSwap }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const initials = user.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const days = user.availability?.map(a => a.day?.slice(0, 3)).join(', ') || 'Flexible';

  const status = swap?.status || null;
  const isSender = swap?.sender?._id === currentUserId || swap?.sender === currentUserId;
  const config = status ? STATUS_CONFIG[status] : null;

  const getActionArea = () => {
    if (!status) {
      return (
        <button className="btn btn-primary btn-sm" onClick={() => onRequestSwap(user)}>
          Request Swap
        </button>
      );
    }

    if (status === 'pending') {
      return (
        <div className="swap-status-action">
          <span className={`badge ${config.badge} swap-status-badge`}>
            {config.icon} {isSender ? 'Awaiting their response' : 'They want to swap with you!'}
          </span>
          <button className="btn btn-ghost btn-sm" disabled title="You have a pending request with this person">
            Request Swap
          </button>
        </div>
      );
    }

    if (status === 'accepted') {
      return (
        <div className="swap-status-action">
          <span className={`badge ${config.badge} swap-status-badge`}>
            {config.icon} Active Swap
          </span>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate(`/messages?userId=${user._id}&name=${encodeURIComponent(user.name)}`)}
          >
            Open Chat →
          </button>
        </div>
      );
    }

    if (status === 'rejected') {
      return (
        <div className="swap-status-action">
          <span className={`badge ${config.badge} swap-status-badge`}>
            {config.icon} {isSender ? 'Your request was declined' : 'You declined this request'}
          </span>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => onRequestSwap(user)}
          >
            Request Again
          </button>
        </div>
      );
    }

    return (
      <button className="btn btn-primary btn-sm" onClick={() => onRequestSwap(user)}>
        Request Swap
      </button>
    );
  };

  return (
    <div className={`user-card ${expanded ? 'expanded' : ''} ${status ? `status-${status}` : ''}`}>
      <div className="user-card-header">
        <div className="avatar" style={{ width: 52, height: 52, fontSize: 18 }}>
          {user.avatar
            ? <img src={user.avatar} alt={user.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
            : initials}
        </div>
        <div className="user-card-info">
          <h3 className="user-card-name">{user.name}</h3>
          {user.location && <p className="user-card-location">📍 {user.location}</p>}
          <div className="user-card-meta">
            <span className="badge badge-gray">⇆ {user.totalSwaps || 0} swaps</span>
            {user.rating > 0 && <span className="badge badge-warning">★ {user.rating.toFixed(1)}</span>}
          </div>
        </div>
      </div>

      {status === 'accepted' && (
        <div className="swap-active-banner">
          🎉 You have an active swap with {user.name.split(' ')[0]}! Go to{' '}
          <button
            className="banner-link"
            onClick={() => navigate('/swaps')}
          >
            My Swaps
          </button>{' '}
          to mark it complete and unlock a new request.
        </div>
      )}

      {status === 'pending' && !isSender && (
        <div className="swap-incoming-banner">
          👋 {user.name.split(' ')[0]} wants to swap skills with you! Check{' '}
          <button className="banner-link" onClick={() => navigate('/swaps')}>
            My Swaps
          </button>{' '}
          to accept or reject.
        </div>
      )}

      {user.bio && <p className="user-card-bio">{user.bio}</p>}

      <div className="user-card-skills">
        <div className="skills-section">
          <span className="skills-label">Offers</span>
          <div className="skills-tags">
            {user.skillsOffered?.slice(0, expanded ? undefined : 3).map((s, i) => (
              <span key={i} className="tag">{s}</span>
            ))}
            {!expanded && user.skillsOffered?.length > 3 && (
              <span className="tag tag-more">+{user.skillsOffered.length - 3}</span>
            )}
          </div>
        </div>
        <div className="skills-divider">⇄</div>
        <div className="skills-section">
          <span className="skills-label">Wants</span>
          <div className="skills-tags">
            {user.skillsWanted?.slice(0, expanded ? undefined : 3).map((s, i) => (
              <span key={i} className="tag tag-wanted">{s}</span>
            ))}
            {!expanded && user.skillsWanted?.length > 3 && (
              <span className="tag tag-wanted tag-more">+{user.skillsWanted.length - 3}</span>
            )}
          </div>
        </div>
      </div>

      <div className="user-card-availability">
        <span className="avail-icon">🗓</span>
        <span className="avail-text">{days}</span>
      </div>

      <div className="user-card-actions">
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Show less' : 'View more'}
        </button>
        {getActionArea()}
      </div>
    </div>
  );
};

export default UserCard;